<script>
    import {tick} from 'svelte';

    import {mapStore} from '@js/stores/mapStore';

    import {pushError} from '@js/actions/errorManagement';

    import {MAP_MODULES} from '@js/modules/maps/mapConstants';
    import MapBuilder from '@js/modules/maps/MapBuilder';

    // Reachable-zone and loop panels: advanced features, loaded once their mode is chosen.
    const PlannerIsochronePanel = () => import('@js/components/planner/PlannerIsochronePanel.svelte');
    const PlannerLoopPanel = () => import('@js/components/planner/PlannerLoopPanel.svelte');

    // Planner-only map behaviours layered on top of the shared BuildMap (rendered inside BuildMap's
    // `mapExtras` snippet, so it has access to the live map via mapStore): fly to a location chosen in
    // the planner search bar, the reachable zone (isochrone), and the floating panel of the reachable zone
    // or the loop, in place of the activity bar. The mode is chosen in the PlannerRouteOptions block under
    // the search.
    let _previousMapLocationValue;

    // What the zone was last synced for (value, start point, activity…), and a token so a late module
    // download never applies an older state over a newer one.
    let _isochroneSyncKey = null;
    let _isochroneSyncMap = null;
    let _isochroneSyncToken = 0;
    let _isochroneStatus = $state(null);
    let _overlaySlot = $state();
    let _overlaySlotHeight = $state(0);
    let _overlayTrack = $state();
    let _mapAreaBottom = $state(0);

    const _setMapLocation = (bbox, center, placeType, name) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.goToLocation(bbox, center, name, {
            showPositionMarker: !name,
            // Use an address / street / house / geolocation result as the route's starting point.
            useStartMarker: ['address', 'street', 'geolocation', 'house'].includes(placeType)
        });
    };

    const _isIsochroneMode = $derived($mapStore.plannerMode === 'isochrone');
    const _isLoopMode = $derived($mapStore.plannerMode === 'loop');
    const _hasModePanel = $derived(_isIsochroneMode || _isLoopMode);

    // A mode panel takes the place of the route-type bar (hidden via a container class), carrying the
    // activity itself, so the two never sit together at the top of the map.
    $effect(() => {
        const container = document.getElementById('planner-map-container');
        if (container) {
            container.classList.toggle('planner-actions-mode', _hasModePanel);
        }
    });

    // The panel's track ends with the map itself, not with the blocks BuildMap lays out under it.
    $effect(() => {
        const mapArea = _overlayTrack?.parentElement?.querySelector(':scope > .map-area');
        if (!mapArea) {
            return;
        }

        const observer = new ResizeObserver(() => {
            _mapAreaBottom = mapArea.offsetTop + mapArea.offsetHeight;
        });
        observer.observe(mapArea);

        return () => observer.disconnect();
    });

    // Map height covered by the panel, kept clear when the map fits the zone.
    const _panelInset = () => {
        const panel = _overlaySlot?.querySelector('.planner-mode-panel');

        return panel ? _overlaySlot.offsetTop + panel.offsetTop + panel.offsetHeight : 0;
    };

    // The zone follows the start point on the map, whenever it was placed: arm the module, then compute
    // from that start point (the module skips a zone it already shows), or only remove the drawing.
    const _syncIsochrone = async (type, value, start) => {
        if (!mapStore.map || !mapStore.map.hasModule('ISOCHRONE')) {
            return;
        }

        const token = ++_isochroneSyncToken;

        // ISOCHRONE (premium) is deferred out of the initial load; pull it in when the user arms it.
        // A chunk that never lands must not throw over the map: report it and leave the zone undrawn.
        const loaded = await MapBuilder.ensureModule(MAP_MODULES.ISOCHRONE)
            .then(() => true)
            .catch((error) => {
                pushError(error);

                return false;
            });

        // The panel laid out first: the zone is fitted below it.
        await PlannerIsochronePanel().catch(() => null);
        await tick();

        // Same guard as every other deferred-module call site (`hasModule && this.method`): the map
        // can also have been replaced while the chunk was downloading.
        if (token !== _isochroneSyncToken || !loaded || typeof mapStore.map?.setIsochrone !== 'function') {
            return;
        }

        mapStore.map.onIsochroneStatusChanged?.((status) => {
            _isochroneStatus = status;
        });
        mapStore.map.setIsochrone(type, value, {topInset: _panelInset()});

        if (start) {
            mapStore.map.computeIsochrone(start);
        } else {
            mapStore.map.clearIsochrone({keepArmed: true});
        }
    };

    // Fly to a location chosen in the planner search bar (PlannerSearch writes mapStore.mapLocation).
    $effect(() => {
        if ($mapStore.mapLocation && ($mapStore.mapLocation.bbox || $mapStore.mapLocation.center) && _previousMapLocationValue !== $mapStore.mapLocation.value) {
            _previousMapLocationValue = $mapStore.mapLocation.value;

            _setMapLocation($mapStore.mapLocation.bbox, $mapStore.mapLocation.center, $mapStore.mapLocation.placeType, $mapStore.mapLocation.name);
        }
    });

    // Reachable zone = mode + value + start point + activity (+ path preference): any change syncs the map,
    // whoever cleared or moved the start point. Leaving the mode disarms it.
    $effect(() => {
        const active = _isIsochroneMode && !!$mapStore.isochroneType && !!$mapStore.isochroneValue;
        const start = $mapStore.startPointLocation;
        const key = active
            ? [$mapStore.isochroneType, $mapStore.isochroneValue, start?.lat, start?.lng, $mapStore.currentRideType, $mapStore.isPreferPathsEnabled].join('|')
            : null;

        if (key === _isochroneSyncKey && mapStore.map === _isochroneSyncMap) {
            return;
        }

        const wasActive = !!_isochroneSyncKey;
        _isochroneSyncKey = key;
        _isochroneSyncMap = mapStore.map;

        if (active) {
            _syncIsochrone($mapStore.isochroneType, $mapStore.isochroneValue, start);
        } else if (wasActive) {
            _isochroneSyncToken++;
            _isochroneStatus = null;

            mapStore.map?.clearIsochrone?.();
        }
    });
</script>

{#if _hasModePanel}
    <!-- Takes no room: the track spans the map, and the dock keeps the panel in view within it while the
         page scrolls over the map. -->
    <div class="planner-overlay-track"
         bind:this={_overlayTrack}
         style:height={_mapAreaBottom ? `${_mapAreaBottom}px` : null}>
        <div class="planner-overlay-dock" style:--planner-overlay-height="{_overlaySlotHeight}px">
            <div class="planner-overlay-slot" bind:this={_overlaySlot} bind:clientHeight={_overlaySlotHeight}>
                {#if _isIsochroneMode}
                    {#await PlannerIsochronePanel() then Panel}
                        <Panel.default status={_isochroneStatus}/>
                    {/await}
                {:else if _isLoopMode}
                    {#await PlannerLoopPanel() then Panel}
                        <Panel.default/>
                    {/await}
                {/if}
            </div>
        </div>
    </div>
{/if}
